import { motion, Variants } from "framer-motion";
import { User, Code, Users, TrendingUp, Handshake } from "lucide-react"; 
import Image from 'next/image';

const fadeIn = (direction: 'left' | 'right', delay: number): Variants => ({
    hidden: {
        opacity: 0,
        x: direction === 'left' ? -20 : 20,
    },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            delay,
            duration: 0.6,
            ease: "easeOut",
        },
    },
});

const keyPoints = [
    { icon: Code, text: "12+ years in Software Engineering, Enterprise Architecture, and SaaS." },
    { icon: TrendingUp, text: "Recognized for scaling engineering teams and designing mission-critical systems." },
    { icon: Handshake, text: "Bridging executive vision with hands-on technical expertise." },
];


export default function AboutSection() {
    return (
        <section id="about" className="py-20 sm:py-24 bg-gray-900 border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-center">
                    
                    {/* --- Profile Picture (Left) - Reverted to Circle --- */}
                    <motion.div
                        className="flex justify-center lg:col-span-1 relative"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7 }}
                    >
                        {/* Background Geometric Shape/Glow - adjusted for circular context */}
                        <div className="absolute inset-4 rounded-full bg-indigo-500 opacity-10 filter blur-2xl z-0"></div>

                        {/* Image Container with Circular Border and Shadow */}
                        <div className="w-72 h-72 rounded-full bg-gray-700 border-4 border-indigo-500 shadow-2xl shadow-indigo-500/30 flex items-center justify-center overflow-hidden relative z-10">
                            <Image 
                                src="/1746988201930.jpeg" 
                                alt="Mohan K. Karki Profile"
                                fill 
                                style={{ objectFit: 'cover', objectPosition: 'center' }} // Centered object position for circular crop
                                className="rounded-full brightness-90" 
                            />
                        </div>
                    </motion.div>
                    
                    {/* --- Content (Right) --- */}
                    <div className="lg:col-span-2">
                        
                        <h2 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                            Bridging Executive Vision & Technical Execution
                        </h2>
                        
                        <div className="mt-8 space-y-6 text-gray-300">
                            <p className="text-lg leading-relaxed">
                                With over 12 years of progressive experience, I've spanned critical roles in software engineering, enterprise architecture, fintech, and advanced SaaS development. My primary focus is on <b>scaling engineering capacity</b>, designing <b>mission-critical systems</b>, and ensuring IT strategy is perfectly aligned with concrete business goals.
                            </p>

                            <p className="text-lg leading-relaxed border-l-4 border-indigo-500 pl-4 py-1 italic text-gray-200 bg-gray-800/50">
                                I am recognized for my ability to seamlessly translate executive vision into robust, secure, and scalable technical solutions. I advise both startups and established enterprises, excelling in CTO, Director, and high-level Consultant roles.
                            </p>
                        </div>

                        {/* Key Expertise Points (Icon List) */}
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                            {keyPoints.map((point, index) => (
                                <motion.div
                                    key={index}
                                    className="flex items-start"
                                    variants={fadeIn('right', 0.8 + index * 0.2)}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.5 }}
                                >
                                    <point.icon className="flex-shrink-0 w-6 h-6 text-indigo-400 mt-1" />
                                    <p className="ml-3 text-base text-gray-400">
                                        {point.text}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}